<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import {
  AisStats,
  AisHits,
  AisSortBy,
  AisConfigure,
  AisSearchBox,
  AisPagination,
  AisInstantSearch,
  AisToggleRefinement,
  AisClearRefinements
} from 'vue-instantsearch/vue3/es'
import { Button } from '@codinglabsau/gooey'

import { Number } from '@codinglabsau/ui'
import { useDebounceFn } from '@vueuse/core'
import { Panel } from '@/Components/Algolia'
import algoliasearch from 'algoliasearch/lite'
import { RefreshCwIcon } from 'lucide-vue-next'
import { apiKey, appId } from '@/Search/utils'
import RangeInput from '@/Components/Algolia/RangeInput.vue'
import RefinementList from '@/Components/Algolia/RefinementList.vue'
import HierarchicalMenu from '@/Components/Algolia/HierarchicalMenu.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  showFilters: {
    type: Boolean,
    default: true,
  },
  defaultIndex: {
    type: String,
    required: true,
  },
  indexOptions: {
    type: Array,
    required: false,
    default: null
  },
  variantFilters: {
    type: Object,
    default: {
      toggleRefinements: [],
      refinementLists: [],
      ranges: []
    }
  },
  limit:{
    type: Number,
    default: 8
  },
  filters: {
    type: String,
    default: ''
  },
  teleportTarget: {
    type: String,
    default: '#aisHits-pagination'
  }
})

const emit = defineEmits(['update:modelValue', 'update:limit', 'hits'])

let instantSearchWrapper = ref(null)

let nodeUiState = computed(() => props.modelValue ? JSON.parse(props.modelValue) : {})

let limit = ref(props.limit);

onMounted(() => {
  if (nodeUiState.value && Object.keys(nodeUiState.value).length > 0) {
    const currentIndex = Object.keys(nodeUiState.value)[0]

    if (nodeUiState.value[currentIndex] && nodeUiState.value[currentIndex].configure) {
      limit.value = nodeUiState.value[currentIndex].configure.hitsPerPage
    }
  } else {
    // no ui state, so set the default ui state
    emit("update:modelValue", JSON.stringify({
      [props.defaultIndex]: {
        configure: {
          hitsPerPage: limit.value
        }
      }
    }))
  }
})
watch(limit, () => {
  if (instantSearchWrapper.value && instantSearchWrapper.value.instantSearchInstance && instantSearchWrapper.value.instantSearchInstance.helper) {
    instantSearchWrapper.value.instantSearchInstance.helper.search()
  }
})

const searchClient = algoliasearch(appId, apiKey)

const onStateChange = ({ uiState, setUiState }) => {
  const currentIndex = Object.keys(uiState)[0]

  if (uiState[currentIndex] && uiState[currentIndex].configure) {
    uiState[currentIndex].configure.hitsPerPage = limit.value
  }

  setUiState(uiState)

  emit("update:modelValue", JSON.stringify(uiState))
}

const debounceHitsChanged = useDebounceFn((items) => {
  emit('hits', items)
}, 200)

const transformItems = (items) => {
  debounceHitsChanged(items)

  return items
}

defineExpose({
  instantSearchWrapper
})
</script>

<template>
  <AisInstantSearch
    ref="instantSearchWrapper"
    :index-name="defaultIndex"
    :initial-ui-state="nodeUiState"
    :search-client="searchClient"
    :on-state-change="onStateChange"
  >
    <AisClearRefinements excluded-attributes class="p-2">
      <template v-slot="{ canRefine, refine }">
        <Button
          v-show="canRefine"
          variant="outline"
          @click.prevent="refine"
        >
          {{ __('Clear Filters') }}
        </Button>
      </template>
    </AisClearRefinements>

    <div class="flex h-full gap-2">
      <AisConfigure
        :hitsPerPage="limit"
        :filters="filters"
      />

      <div class="w-full sm:w-2/5 overflow-y-auto p-2 h-full space-y-2 max-h-full md:max-h-[800px] xl:max-h-[1000px]" v-if="showFilters">
        <Panel>
          <template #header>
            <span>{{ __('Search') }}</span>
          </template>
          <AisSearchBox>
            <template v-slot="{ currentRefinement, isSearchStalled, refine }">
              <input
                type="search"
                :value="currentRefinement"
                @input="refine($event.currentTarget.value)"
                class="mt-1 block w-full rounded-md border px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-primary-300 focus:ring-primary-200/50 sm:text-sm border-gray-300">
              <span :hidden="!isSearchStalled" class="text-gray-600">
                <RefreshCwIcon class="size-4 inline animate-spin"/>
                {{ __('Searching...') }}
              </span>
            </template>
          </AisSearchBox>
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Sort By') }}</span>
          </template>
          <AisSortBy
            v-if="indexOptions !== null"
            :items="indexOptions"
          />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Limit') }}</span>
          </template>
          <Number
            id="limit"
            v-model="limit"
            :min="4"
            :max="16"
            :step="1"
          />
        </Panel>

        <div>
          <Panel>
            <AisToggleRefinement label="No Reserve" attribute="no_reserve">
              <template #default="{ value, canRefine, refine, createUrl, sendEvent }">
                <div @click.prevent="refine(value)"
                     class="flex items-center text-base sm:text-sm cursor-pointer p-1 hover:bg-gray-100">
                  <input :checked="value.isRefined" disabled type="checkbox"
                         class="flex-shrink-0 h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500 pointer-events-none">
                  <div class="flex items-center justify-between w-full">
                    <div class="ml-2 capitalize min-w-0 flex-1 text-gray-600">{{ __('No Reserve') }}</div>
                    <div class="ml-2 capitalize min-w-0 text-gray-600 text-right rounded-lg bg-gray-100 px-2">
                      {{ value.count ? value.count.toLocaleString() : 0 }}
                    </div>
                  </div>
                </div>
              </template>
            </AisToggleRefinement>
          </Panel>
          <Panel>
            <AisToggleRefinement label="On Sale" attribute="on_sale">
              <template #default="{ value, canRefine, refine, createUrl, sendEvent }">
                <div @click.prevent="refine(value)"
                     class="flex items-center text-base sm:text-sm cursor-pointer p-1 hover:bg-gray-100">
                  <input :checked="value.isRefined" disabled type="checkbox"
                         class="flex-shrink-0 h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500 pointer-events-none">
                  <div class="flex items-center justify-between w-full">
                    <div class="ml-2 capitalize min-w-0 flex-1 text-gray-600">{{ __('On Sale') }}</div>
                    <div class="ml-2 capitalize min-w-0 text-gray-600 text-right rounded-lg bg-gray-100 px-2">
                      {{ value.count ? value.count.toLocaleString() : 0 }}
                    </div>
                  </div>
                </div>
              </template>
            </AisToggleRefinement>
          </Panel>
          <Panel>
            <AisToggleRefinement label="Make an offer" attribute="make_an_offer">
              <template #default="{ value, canRefine, refine, createUrl, sendEvent }">
                <div @click.prevent="refine(value)"
                     class="flex items-center text-base sm:text-sm cursor-pointer p-1 hover:bg-gray-100">
                  <input :checked="value.isRefined" disabled type="checkbox"
                         class="flex-shrink-0 h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500 pointer-events-none">
                  <div class="flex items-center justify-between w-full">
                    <div class="ml-2 capitalize min-w-0 flex-1 text-gray-600">{{ __('Make an offer') }}</div>
                    <div class="ml-2 capitalize min-w-0 text-gray-600 text-right rounded-lg bg-gray-100 px-2">
                      {{ value.count ? value.count.toLocaleString() : 0 }}
                    </div>
                  </div>
                </div>
              </template>
            </AisToggleRefinement>
          </Panel>
          <Panel v-for="variant in variantFilters.toggleRefinements">
            <AisToggleRefinement :label="variant.label" :attribute="variant.attribute">
              <template #default="{ value, canRefine, refine, createUrl, sendEvent }">
                <div @click.prevent="refine(value)"
                     class="flex items-center text-base sm:text-sm cursor-pointer p-1 hover:bg-gray-100">
                  <input :checked="value.isRefined" disabled type="checkbox"
                         class="flex-shrink-0 h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500 pointer-events-none">
                  <div class="flex items-center justify-between w-full">
                    <div class="ml-2 capitalize min-w-0 flex-1 text-gray-600">{{ variant.label }}</div>
                    <div class="ml-2 capitalize min-w-0 text-gray-600 text-right rounded-lg bg-gray-100 px-2">
                      {{ value.count ? value.count.toLocaleString() : 0 }}
                    </div>
                  </div>
                </div>
              </template>
            </AisToggleRefinement>
          </Panel>
        </div>

        <Panel>
          <template #header>
            <span>{{ __('Categories') }}</span>
          </template>
          <HierarchicalMenu
            :attributes="['categories.lvl0', 'categories.lvl1', 'categories.lvl2', 'categories.lvl3']"
            :show-parent-level="true"
            show-more
            :limit="5"
          />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Product Type') }}</span>
          </template>
          <RefinementList
            attribute="type"
          />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Listing Type Tier') }}</span>
          </template>
          <RefinementList
            attribute="listing_type_tier"
          />
        </Panel>

        <Panel v-for="variant in variantFilters.refinementLists">
          <template #header>
            <span>{{ variant.label }}</span>
          </template>
          <RefinementList
            :attribute="variant.attribute"
          />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Price') }}</span>
          </template>
          <RangeInput attribute="price" />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Length') }} (mm)</span>
          </template>
          <RangeInput attribute="length" />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Width') }} (mm)</span>
          </template>
          <RangeInput attribute="width" />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Depth') }} (mm)</span>
          </template>
          <RangeInput attribute="depth" />
        </Panel>

        <Panel>
          <template #header>
            <span>{{ __('Weight') }} (carats)</span>
          </template>
          <RangeInput attribute="weight" />
        </Panel>

        <Panel v-for="variant in variantFilters.ranges">
          <template #header>
            <span>{{ variant.label }}</span>
          </template>
          <RangeInput :attribute="variant.attribute" :precision="variant.precision" />
        </Panel>
      </div>
      <div class="sm:w-3/5">
        <AisHits :transform-items="transformItems">
          <template v-slot="{ items }">
            <slot name="hits" :items="items"></slot>
            <AisStats v-if="teleportTarget" v-slot="{ nbHits }">
              <Teleport :to="teleportTarget">
                <div class="flex justify-between items-center border-t border-gray-200 my-2 py-2 gap-2 w-full">
                  <span class="text-xs md:text-sm text-gray-500">
                    {{ __('Showing :total of :hits items', { total: items.length, hits: nbHits }) }}
                  </span>
                  <AisPagination
                    :class-names="{
                        'ais-Pagination':'flex items-center',
                        'ais-Pagination-list': 'flex items-center gap-1',
                        'ais-Pagination-item': 'inline-flex items-center rounded-md px-2 py-1 text-sm font-semibold text-gray-700 hover:bg-primary-100 hover:text-gray-800 focus:z-20 focus:outline-offset-0',
                        'ais-Pagination-item--selected': 'bg-primary-600 text-white relative inline-flex items-center rounded-md px-2 py-1 text-sm font-semibold focus:z-20 focus:outline-offset-0',
                        'ais-Pagination-link': 'flex items-center justify-center'
                    }"
                  />
                </div>
              </Teleport>
            </AisStats>
          </template>
        </AisHits>
      </div>
    </div>
  </AisInstantSearch>
</template>
